import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CooperativeLogo } from "@/components/ui/cooperative-logo";
import { Building2, MapPin, Mail, Phone, Database } from "lucide-react";
import { useAuth } from "@/context/auth.context";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { canDownloadSystemBackup } from "@/lib/utils";

export default function GeneralSettings() {
  const { user } = useAuth();
  const [backupLoading, setBackupLoading] = useState(false);

  const downloadDatabaseBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get("/system/backup/sql", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/sql" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ikimina-database-backup-${new Date().toISOString().slice(0, 10)}.sql`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Database backup downloaded");
    } catch (e: any) {
      let msg = e?.response?.data?.message || e?.message || "Backup failed";
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.message || text || msg;
        } catch {
          /* keep msg */
        }
      }
      toast.error(typeof msg === "string" ? msg : "Backup failed");
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <div className="p-3">
      <div className="container max-w-3xl space-y-4">
        <div>
          <h1 className="text-base font-semibold tracking-tight">
            Organization Settings
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage your organization's general information and settings.
          </p>
        </div>

        <Card className="rounded-md shadow-none">
          <CardHeader>
            <CardTitle>General Information</CardTitle>
            <CardDescription>
              Update your organization's profile and public information.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
              <CooperativeLogo size={80} circular={false} className="border-2 border-gray-200 shadow-sm" />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Upload or change logo in Settings &rarr; Organization &rarr; Cooperative Branding.
                </p>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="orgName">Organization name</Label>
              <Input id="orgName" defaultValue="Acme Inc." />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="orgDescription">Description</Label>
              <Textarea
                id="orgDescription"
                placeholder="Describe your organization"
                defaultValue="Acme Inc. is a leading provider of innovative solutions for businesses of all sizes."
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">
                Brief description of your organization. This will appear on your
                public profile.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="industry">Industry</Label>
                <Select defaultValue="technology">
                  <SelectTrigger id="industry">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="size">Organization size</Label>
                <Select defaultValue="medium">
                  <SelectTrigger id="size">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">1-10 members</SelectItem>
                    <SelectItem value="medium">11-50 members</SelectItem>
                    <SelectItem value="large">51-200 members</SelectItem>
                    <SelectItem value="enterprise">201+ members</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Address</Label>
              <div className="relative">
                <MapPin className="absolute z-10 left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Street address"
                  defaultValue="123 Main St"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="email">Contact email</Label>
                <div className="relative">
                  <Mail className="absolute z-10 left-2.5 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    className="pl-8"
                    type="email"
                    defaultValue="caisse.iprcwest@gmail.com"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone">Contact phone</Label>
                <div className="relative">
                  <Phone className="absolute z-10 left-2.5 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    className="pl-8"
                    type="tel"
                    defaultValue="0793502827"
                  />
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between border-t px-6 py-4">
            <Button size="sm" variant="outline">
              Cancel
            </Button>
            <Button size="sm">Save organization</Button>
          </CardFooter>
        </Card>

        {canDownloadSystemBackup(user) ? (
          <Card className="rounded-md shadow-none border-amber-200/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-5 w-5 text-amber-700" />
                System data backup
              </CardTitle>
              <CardDescription>
                Download a full PostgreSQL SQL dump for disaster recovery. Store this file
                securely; it contains the entire database including credentials.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="secondary"
                disabled={backupLoading}
                onClick={() => void downloadDatabaseBackup()}
              >
                {backupLoading ? "Preparing backup…" : "Download database backup (.sql)"}
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
